import {
  FileFragment,
  RichTextFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { ICartState } from '@/ui/store/cart/types';
import { componentByTypename } from '@/ui/utils/componentByTypename';
import DigitalProductTypes from '@/constants/digitalProductTypes';
import { apiPaths } from '@/constants/apiPaths';
import { DeliveryTimeRange } from '@/integrations/crystallize/types/localization/DeliveryTimeRange';
import { ParsedProductVariant } from '../components/pages/ProductDetail/types/ParsedProductVariant';
import { DigitalProductCrystallizeData } from '../components/pages/ProductDetail/types/DigitalProductCrystallizeData';
import { BaseProduct } from './BaseProductService';
import { isDigitalProductVariantInStock } from './utils/isVariantInStock';

export class DigitalProduct<
  T extends DigitalProductCrystallizeData = DigitalProductCrystallizeData,
  V extends ParsedProductVariant = ParsedProductVariant,
> extends BaseProduct<T, V> {
  shortDescription?: RichTextFragment;

  description?: RichTextFragment;

  note?: RichTextFragment;

  requiresActivation?: boolean;

  file?: FileFragment;

  nukiIdHeading?: RichTextFragment;

  nukiIdSubtext?: RichTextFragment;

  constructor(data: T, cartStore: ICartState) {
    super(data, cartStore);

    this.shortDescription =
      componentByTypename(data.shortDescription?.content, 'RichTextContent') || undefined;
    this.description =
      componentByTypename(data.description?.content, 'RichTextContent') || undefined;
    this.note = componentByTypename(data.note?.content, 'RichTextContent') || undefined;
    this.requiresActivation =
      componentByTypename(data.showChooser?.content, 'BooleanContent')?.value || false;

    this.nukiIdHeading =
      componentByTypename(data.nukiIdHeading?.content, 'RichTextContent') || undefined;

    this.nukiIdSubtext =
      componentByTypename(data.nukiIdSubtext?.content, 'RichTextContent') ?? undefined;

    this.variants = (data.variants?.map(super.parseProductVariant) as V[]) || [];
  }

  isSkuInStock(sku: string) {
    const productVariant = this.variants.find((variant) => variant.sku === sku);
    return isDigitalProductVariantInStock(productVariant);
  }

  isVariantInStock(productVariant: ParsedProductVariant) {
    return isDigitalProductVariantInStock(productVariant);
  }

  isLowStock(): boolean {
    return false;
  }

  getStandardDeliveryTimeRange(): DeliveryTimeRange {
    return {
      min: 0,
      max: 0,
    };
  }

  getLowStockDeliveryTimeRange(): DeliveryTimeRange {
    return {
      min: 0,
      max: 0,
    };
  }

  getDeliveryTimeRange(): DeliveryTimeRange {
    return {
      min: 0,
      max: 0,
    };
  }

  get isAnyVariantInStock() {
    return this.variants.some((variant) => this.isVariantInStock(variant));
  }

  addToCart(
    currentVariant: V,
    options?: {
      nukiIds?: string[];
      activationCode?: string;
      directPurchase?: boolean;
      quantity?: number;
    },
  ): void {
    if (options?.nukiIds && typeof options.nukiIds === 'object' && options.nukiIds.length > 0) {
      // Add product to cart for each nukiID inserted.
      // NOTE: when nukiIds are provided, `quantity` is ignored — one item is added per nukiId.
      options.nukiIds.forEach((nukiId) => {
        this.cartStore.addProduct(this, currentVariant, { nukiId, fixedQuantity: true });
      });
    } else {
      this.cartStore.addProduct(this, currentVariant, {
        ...options,
        fixedQuantity: true,
        quantity: options?.quantity,
      });
    }
    if (options?.directPurchase === true) {
      return;
    }
    this.cartStore.toggleCartDisplay();
  }

  getProductTypeByPath() {
    const splitPath = this.path.split('/');
    return splitPath[splitPath.length - 1];
  }

  getValidationPath(productType: string) {
    let validationPath = '';
    if (productType === DigitalProductTypes.GuaranteePlus) {
      validationPath = apiPaths.devices.guaranteePlus.validate;
    } else if (productType === DigitalProductTypes.RemoteAccess) {
      validationPath = apiPaths.devices.remoteAccess.validate;
    }
    return validationPath;
  }
}
